"use client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import react, { useCallback, useEffect, useState } from "react";
import AlertTriangle from "@crehana/compass.icons.outline/AlertTriangle";
import ArrowIosLeft from "@crehana/compass.icons.outline/ArrowIosLeft";
import ArrowIosRight from "@crehana/compass.icons.outline/ArrowIosRight";
import { useTranslation } from "@crehana/i18n";
import { Button } from "../../../Button/index.js";
import { EmptyState } from "../../../EmptyState/index.js";
import { CommandItem } from "../../../_shadcn-native-components/command.js";
import { Checkbox } from "../../../Checkbox/index.js";
import { cn } from "../../../utils.js";
import { useDropdownContext } from "../../context.js";
import { DropdownBaseList } from "../../DropdownBaseList.js";
import i18n from "../../i18n/index.js";
import { PrimitiveDropdownLoading } from "../../primitive-dropdown/index.js";
function AsyncLeveledDropdown({ categories, onCategorySelect, loading = false, onSelectionChange, defaultValues = [], trackByCategory = false, renderSubItem, multiselect = true, ...props }) {
    const { languageCode } = useTranslation();
    const dropdownContext = useDropdownContext();
    const t = i18n[languageCode];
    if (!dropdownContext) {
        console.error('AsyncLeveledDropdown: Missing DropdownProvider context. Please wrap the component with DropdownProvider or @crehana/compass.ui/v2/Dropdown');
        return null;
    }
    const { values, setItems, setValues } = dropdownContext;
    const [selectedCategory, setSelectedCategory] = useState();
    const [subItems, setSubItems] = useState([]);
    const [isLoadingItems, setIsLoadingItems] = useState(false);
    const [isError, setIsError] = useState(false);
    useEffect(()=>{
        setItems(categories.map((item)=>({
                ...item,
                type: 'checkbox'
            })));
    }, [
        categories,
        setItems
    ]);
    useEffect(()=>{
        if (defaultValues.length > 0) setValues(defaultValues);
    }, [
        defaultValues,
        setValues
    ]);
    const fetchCategoryItems = useCallback(async (categoryId)=>{
        setIsLoadingItems(true);
        setIsError(false);
        onCategorySelect(categoryId).then((items)=>{
            setSubItems(items);
            setItems(items);
        }).catch((error)=>{
            console.error('Error loading category items:', error);
            setSubItems([]);
            setIsError(true);
        }).finally(()=>{
            setIsLoadingItems(false);
        });
    }, [
        onCategorySelect,
        setItems
    ]);
    const handleCategorySelect = useCallback((categoryId)=>{
        setSelectedCategory(categoryId);
        fetchCategoryItems(categoryId);
    }, [
        fetchCategoryItems
    ]);
    const handleRetry = useCallback(()=>{
        if (selectedCategory) fetchCategoryItems(selectedCategory);
    }, [
        selectedCategory,
        fetchCategoryItems
    ]);
    const handleSelection = useCallback((value, state)=>{
        const categoryPrefix = trackByCategory ? `${selectedCategory}.` : '';
        const processedValue = `${categoryPrefix}${value}`;
        const newSelection = multiselect ? state ? [
            ...values,
            processedValue
        ] : values?.filter((item)=>item !== processedValue) : [
            processedValue
        ];
        setValues(newSelection);
        if (onSelectionChange) if (trackByCategory) {
            const categorizedValues = newSelection.reduce((acc, curr)=>{
                const [category, value] = String(curr).split('.');
                if (!acc[category]) acc[category] = [];
                acc[category].push(value);
                return acc;
            }, {});
            onSelectionChange(categorizedValues);
        } else onSelectionChange(newSelection);
    }, [
        values,
        setValues,
        onSelectionChange,
        selectedCategory,
        trackByCategory,
        multiselect
    ]);
    const handleClearCategory = useCallback(()=>{
        if (subItems.length > 0) {
            const categoryPrefix = trackByCategory ? `${selectedCategory}.` : '';
            const newSelection = values?.filter((item)=>!subItems.map((subcategory)=>`${categoryPrefix}${subcategory.value}`).includes(String(item)));
            setValues(newSelection);
            if (onSelectionChange) if (trackByCategory) {
                const categorizedValues = newSelection.reduce((acc, curr)=>{
                    const [category, value] = String(curr).split('.');
                    if (!acc[category]) acc[category] = [];
                    acc[category].push(value);
                    return acc;
                }, {});
                onSelectionChange(categorizedValues);
            } else onSelectionChange(newSelection);
        }
    }, [
        subItems,
        values,
        setValues,
        onSelectionChange,
        selectedCategory,
        trackByCategory
    ]);
    const selectedValues = values.filter((value)=>{
        if (trackByCategory) return String(value).startsWith(`${selectedCategory}.`);
        return subItems.some((item)=>item.value === value);
    });
    const defaultRenderSubItem = useCallback(({ item, isSelected, onSelect })=>/*#__PURE__*/ jsxs(CommandItem, {
            className: "tw-justify-start",
            value: item.value.toString(),
            removeCheck: true,
            onSelect: ()=>onSelect(item.value, !isSelected),
            children: [
                multiselect && /*#__PURE__*/ jsx(Checkbox, {
                    checked: isSelected
                }),
                item.label
            ]
        }, item.value), [
        multiselect
    ]);
    return /*#__PURE__*/ jsx(DropdownBaseList, {
        className: props.className,
        children: selectedCategory ? /*#__PURE__*/ jsxs("div", {
            className: "tw-relative tw-flex tw-flex-col",
            children: [
                /*#__PURE__*/ jsxs("button", {
                    onClick: ()=>{
                        setSelectedCategory(void 0);
                        setSubItems([]);
                        setIsLoadingItems(false);
                        setIsError(false);
                    },
                    className: cn('tw-flex tw-items-center tw-gap-0.4 tw-sticky tw-top-0 tw-z-2', 'tw-bg-neutral-light-100 dark:tw-bg-neutral-dark-700', 'tw-w-full tw-text-left tw-py-0.8 tw-px-2.4 tw-font-caption tw-text-primary-light-500 dark:tw-text-secondary-light-500', 'hover:tw-text-primary-light-400 hover:dark:tw-text-secondary-light-400 hover:tw-bg-neutral-light-300 dark:hover:tw-bg-neutral-dark-600'),
                    children: [
                        /*#__PURE__*/ jsx(ArrowIosLeft, {
                            size: 20,
                            className: "tw-text-primary-light-500 dark:tw-text-secondary-light-500"
                        }),
                        t.DROPDOWN_OPTIONS_BACK_BUTTON
                    ]
                }),
                isError && /*#__PURE__*/ jsx(EmptyState, {
                    className: "tw-h-full tw-w-full !tw-p-0.8 !tw-m-0",
                    icon: /*#__PURE__*/ jsx(AlertTriangle, {
                        size: 20,
                        className: "tw-text-neutral-light-600"
                    }),
                    title: t.DROPDOWN_ERROR_TITLE,
                    description: t.DROPDOWN_ERROR_DESCRIPTION,
                    primaryAction: /*#__PURE__*/ jsx(Button, {
                        type: "button",
                        variant: "delete",
                        size: "xs",
                        onClick: handleRetry,
                        children: t.DROPDOWN_ERROR_RETRY
                    }),
                    small: true
                }),
                isLoadingItems ? /*#__PURE__*/ jsx(PrimitiveDropdownLoading, {
                    className: "tw-h-20",
                    loading: true
                }) : /*#__PURE__*/ jsxs(Fragment, {
                    children: [
                        subItems.map((item)=>{
                            const categoryPrefix = trackByCategory ? `${selectedCategory}.` : '';
                            const processedValue = `${categoryPrefix}${item.value}`;
                            const isSelected = values.includes(processedValue);
                            return /*#__PURE__*/ jsx(react.Fragment, {
                                children: (renderSubItem || defaultRenderSubItem)({
                                    item,
                                    isSelected,
                                    onSelect: (value, state)=>handleSelection(value, state)
                                })
                            }, item.value);
                        }),
                        multiselect && subItems.length > 0 && /*#__PURE__*/ jsx("button", {
                            onClick: handleClearCategory,
                            className: cn('tw-w-full tw-sticky tw-bottom-0 tw-text-left tw-py-1.2 tw-px-2.4 tw-font-caption tw-text-primary-light-500 dark:tw-text-secondary-light-500', 'tw-bg-neutral-light-100 dark:tw-bg-neutral-dark-700', 'hover:tw-text-primary-light-400 hover:dark:tw-text-secondary-light-400 hover:tw-bg-neutral-light-300 dark:hover:tw-bg-neutral-dark-600'),
                            children: t.DROPDOWN_OPTIONS_ERASE_ALL
                        })
                    ]
                })
            ]
        }) : categories.map((item)=>/*#__PURE__*/ jsxs(CommandItem, {
                value: item.value.toString(),
                removeCheck: true,
                onSelect: ()=>handleCategorySelect(item.value),
                children: [
                    /*#__PURE__*/ jsxs("div", {
                        className: "tw-flex tw-items-center tw-gap-0.8",
                        children: [
                            item.label,
                            selectedValues.length > 0 && /*#__PURE__*/ jsx("span", {
                                className: "tw-rounded tw-flex tw-items-center tw-justify-center tw-w-2 tw-h-2 tw-font-body5 tw-bg-primary-light-500 tw-text-neutral-light-100",
                                children: selectedValues.length
                            })
                        ]
                    }),
                    /*#__PURE__*/ jsx(ArrowIosRight, {
                        size: 24,
                        className: "tw-text-primary-light-500 dark:tw-text-secondary-light-500"
                    })
                ]
            }, item.value))
    });
}
export { AsyncLeveledDropdown };
